///
/// EarthBrowser - Satellite Browser Renderer
///
/// Movable window / dock tab listing the satellites on the globe. The list
/// follows the satellite sets chosen in the Layers panel (chip strip on the
/// Satellites row), so the table and the globe always agree; the status
/// line names the sets. There is no selector here on purpose.
///
/// Created by Matt Giger on December 20, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useCallback, useMemo, useState, useEffect, memo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { sendDatasetAction } from '@/redux/earthBrowserSlice'
import { updateDatasetWindow, closeDatasetWindow, type DatasetBrowserWindowState } from '@/redux/dialogSlice'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { DatasetVisibilityToggle } from '@/components/windows/DatasetVisibilityToggle'
import { DataBrowserView, type DataBrowserViewHandle } from '@/components/data-browser/DataBrowserView'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { DataProviderRegistry } from '@/data-providers/DataProviderRegistry'
import { SatelliteDataProvider } from '@/data-providers/SatelliteDataProvider.tsx'
import { cSatelliteSets, groupIdsForSets } from '@/earthbrowser/dataset-distribution/satellites/satelliteSets'
import { SatelliteTLETable } from '@/earthbrowser/dataset-distribution/satellites/SatelliteTLETable'
import { useDatasetLayerRefresh } from '@/hooks/useDatasetLayerRefresh'
import type { SatelliteEntry } from '@shared-types/satellite-entry'
import type { DataRow } from '@/data-providers/types'
import { Satellite } from 'lucide-react'
import { isMobile } from '@/earthbrowser/utils/platform'

interface SatelliteBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const SatelliteBrowserRenderer = memo(function SatelliteBrowserRenderer({
	windowId,
	windowState,
}: SatelliteBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)
	const satellitesVisible = visibleDatasets.includes("satellite")
	const satelliteSets = useAppSelector(state => state.earthbrowser.satelliteSets)
	const groupIds = useMemo(() => groupIdsForSets(satelliteSets), [satelliteSets])

	// Get dataset layer manager from Redux
	const layerManager = useAppSelector(state => state.datasetScene.layerManager)

	// Local state
	const [isReady, setIsReady] = useState(false)
	const [totalCount, setTotalCount] = useState(0)
	const [dataVersion, setDataVersion] = useState(0)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)
	const providerRef = useRef<SatelliteDataProvider | null>(null)

	// Catalog size for the title
	useEffect(() => {
		SatelliteTLETable.getCount()
			.then(setTotalCount)
			.catch(error => console.error('Failed to count satellites:', error))
	}, [])

	// Initialize provider (lightweight - no data preloading)
	useEffect(() => {
		const oldProvider = DataProviderRegistry.get(windowId)
		if (oldProvider) {
			void oldProvider.dispose?.()
			DataProviderRegistry.unregister(windowId)
		}

		const provider = new SatelliteDataProvider({
			id: windowId,
			name: 'Satellites',
			groupIds,
		})

		void provider.initialize().then(() => {
			DataProviderRegistry.register(windowId, provider)
			providerRef.current = provider
			setIsReady(true)
		})
	}, [windowId]) // eslint-disable-line react-hooks/exhaustive-deps

	// Follow the Layers panel: re-query when the chosen sets change
	useEffect(() => {
		if (!providerRef.current) return
		providerRef.current.setGroupIds(groupIds)
		setDataVersion(v => v + 1)
	}, [groupIds])

	// Re-key the view whenever the satellites layer refreshes after a sync;
	// the provider queries the local table live, so a remount picks up new TLEs
	useDatasetLayerRefresh('satellites', () => {
		setDataVersion(v => v + 1)
	})

	// Sync scene layer visibility with Redux state
	useEffect(() => {
		if (layerManager) {
			layerManager.setLayerVisible('satellites', satellitesVisible)
		}
	}, [layerManager, satellitesVisible])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			const provider = DataProviderRegistry.get(windowId)
			if (provider) {
				void provider.dispose?.()
				DataProviderRegistry.unregister(windowId)
			}
		}
	}, [windowId])

	// Handle satellite row click
	const handleRowClick = useCallback((row: DataRow) => {
		const satellite = row as unknown as SatelliteEntry

		// Ensure satellites are visible on the map
		if (!satellitesVisible) {
			dispatch(sendDatasetAction({
				type: "satellite",
				action: "enable"
			}))
			if (layerManager) {
				layerManager.setLayerVisible('satellites', true)
			}
		}

		if (layerManager) {
			layerManager.focusSceneObject('satellites', satellite.object_id)
		}

		if (isMobile()) {
			dispatch(closeDatasetWindow(windowId))
		}
	}, [layerManager, satellitesVisible, dispatch, windowId])

	// Handle refresh
	const handleRefresh = useCallback(() => {
		setDataVersion(v => v + 1)
	}, [])

	// Status line names the sets on the globe
	const setsSummary = useMemo(() => {
		const chosen = cSatelliteSets.filter(set => satelliteSets.includes(set.id))
		if (chosen.length === 0) return 'No sets selected'
		if (chosen.length === cSatelliteSets.length) return 'All sets'
		if (chosen.length <= 3) return chosen.map(set => set.name).join(', ')
		return `${chosen.length} sets`
	}, [satelliteSets])

	useEffect(() => {
		dispatch(updateDatasetWindow({
			id: windowId,
			updates: { status: setsSummary }
		}))
	}, [setsSummary, windowId, dispatch])

	// Title suffix showing count
	const titleSuffix = useMemo(() => (
		<span className="text-xs text-white/40 font-normal">
			{totalCount.toLocaleString()}
		</span>
	), [totalCount])

	// Content
	const content = useMemo(() => {
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isReady) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					key={`satellite-browser-${dataVersion}`}
					provider={provider}
					onRowClick={handleRowClick}
					onRefresh={handleRefresh}
					totalDatabaseCount={totalCount}
					searchPlaceholder="Search by name, NORAD ID..."
				/>
			)
		}

		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-purple-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
					</div>
					<p className="text-base font-medium text-white">
						Loading Satellites
					</p>
					<p className="text-sm text-white/60">
						Initializing...
					</p>
				</div>
			</div>
		)
	}, [windowId, isReady, handleRowClick, handleRefresh, totalCount, dataVersion])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Satellite className="w-5 h-5" />}
			iconColor="text-purple-400"
			titlebarPrefix={<DatasetVisibilityToggle windowType="satellite" />}
			titleSuffix={titleSuffix}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})
